import type { TenantUsageSummaryResponse } from '@contract-analyzer/api-client';
import { useContext, useEffect, useRef, useState } from 'react';

import type { ExtractionSpendPreview } from '@/components/analysis-workspace-v2/document-picker-sheet';

import { TenantCtx } from '@/hooks/tenant-context';
import { apiClient } from '@/lib/api';
import { deriveTrialPhase, trialRefreshDelayMs, type TrialPhase } from '@/lib/trial-phase';

/**
 * Whether billing is ready to carry work past the free allowance:
 * `usable` — an active or approved charge code exists;
 * `pending` — a charge code is submitted and awaiting approval;
 * `none` — nothing is set up yet.
 */
type ChargeCodeReadiness = TenantUsageSummaryResponse['charge_code_status'];

/** The remaining lifetime extraction allowance of a quota-limited workspace. */
export interface ExtractionRunwaySnapshot {
  readonly quota: number;
  readonly used: number;
  readonly remaining: number;
}

export interface WorkspaceBillingPosture {
  readonly chargeCodeReadiness: ChargeCodeReadiness;
  /**
   * The free allowance is not enforced for this workspace right now: an
   * unexpired trial or a usable charge code. Mirrors the server's own
   * exemption rule, served on the usage summary every member can read.
   */
  readonly isPaidWorkspace: boolean;
  /** `null` for exempt workspaces and workspaces without an extraction quota. */
  readonly runway: ExtractionRunwaySnapshot | null;
  /**
   * Where the admin-granted trial stands, so surfaces can say when paid
   * access ends, or that it ended and that is why the allowance now applies.
   */
  readonly trial: TrialPhase;
}

export function deriveWorkspaceBillingPosture(
  usage: Pick<
    TenantUsageSummaryResponse,
    | 'extraction_quota'
    | 'extraction_quota_used'
    | 'extraction_quota_applies'
    | 'charge_code_status'
    | 'trial_expires_at'
  >,
  now: Date = new Date(),
): WorkspaceBillingPosture {
  const chargeCodeReadiness = usage.charge_code_status;
  const isPaidWorkspace = !usage.extraction_quota_applies;
  const trial = deriveTrialPhase(usage, now);

  const quota = usage.extraction_quota;
  if (isPaidWorkspace || quota <= 0) {
    return { chargeCodeReadiness, isPaidWorkspace, runway: null, trial };
  }

  const used = Math.min(Math.max(usage.extraction_quota_used, 0), quota);
  return {
    chargeCodeReadiness,
    isPaidWorkspace,
    runway: { quota, remaining: quota - used, used },
    trial,
  };
}

/**
 * Adapts a loaded runway into the document picker's spend-preview shape.
 * Returns `undefined` — hiding the preview — while loading, for exempt
 * workspaces, and when the per-document cost is not yet known.
 */
export function toExtractionSpend(
  state: ExtractionRunwayState,
  unitsPerDocument: number,
): ExtractionSpendPreview | undefined {
  if (state.status !== 'ready' || state.runway === null || unitsPerDocument <= 0) {
    return undefined;
  }
  return {
    quota: state.runway.quota,
    remaining: state.runway.remaining,
    unitsPerDocument,
  };
}

export type ExtractionRunwayState =
  | { readonly status: 'disabled' }
  | { readonly status: 'loading' }
  | { readonly status: 'unavailable' }
  | ({
      readonly status: 'ready';
      readonly usage: TenantUsageSummaryResponse;
    } & WorkspaceBillingPosture);

const runwayListeners = new Set<() => void>();

/**
 * Tell every mounted runway that extraction units were just reserved, so
 * always-visible surfaces such as the sidebar refetch instead of showing the
 * count from before the reservation until the next remount.
 */
export function notifyExtractionRunwayChanged(): void {
  for (const listener of runwayListeners) {
    listener();
  }
}

/**
 * Loads the workspace's usage and billing posture for quota-aware surfaces
 * (sidebar runway, extraction spend preview, quota dialog). Pass `enabled`
 * to defer the fetch until the surface actually shows, e.g. a sheet opening.
 * Everything comes from the usage summary, which every member can read;
 * no privileged tenant detail is needed.
 */
export function useExtractionRunway({
  enabled,
  tenantId: explicitTenantId,
  now,
}: {
  readonly enabled: boolean;
  /**
   * Surfaces outside the tenant-scoped shell (Settings tabs) know the
   * workspace from the signed-in user rather than from `TenantCtx`.
   */
  readonly tenantId?: string | null;
  /** The clock the trial phase is judged against; the wall clock by default. */
  readonly now?: Date | undefined;
}): ExtractionRunwayState {
  const tenant = useContext(TenantCtx);
  const tenantId = explicitTenantId ?? tenant?.tenantId ?? null;
  // Loading from the first render when a fetch will run: starting `disabled`
  // painted a frame with no usage at all before the skeleton, so the footer
  // popped in twice and a capture could land on the empty frame.
  const [state, setState] = useState<ExtractionRunwayState>(() =>
    enabled && tenantId !== null ? { status: 'loading' } : { status: 'disabled' },
  );
  const [generation, setGeneration] = useState(0);
  // Kept in a ref so a caller passing a fresh `new Date()` each render does not
  // retrigger the fetch effect; it is read when the response arrives.
  const nowRef = useRef(now);
  useEffect(() => {
    nowRef.current = now;
  }, [now]);

  useEffect(() => {
    const bump = () => {
      setGeneration((current) => current + 1);
    };
    runwayListeners.add(bump);
    return () => {
      runwayListeners.delete(bump);
    };
  }, []);

  useEffect(() => {
    if (!enabled || tenantId === null) {
      setState({ status: 'disabled' });
      return;
    }

    let cancelled = false;
    setState({ status: 'loading' });

    async function fetchPosture(tid: string) {
      try {
        const usage = await apiClient.getTenantUsage(tid);
        if (cancelled) return;
        setState({
          status: 'ready',
          usage,
          ...deriveWorkspaceBillingPosture(usage, nowRef.current ?? new Date()),
        });
      } catch {
        if (!cancelled) setState({ status: 'unavailable' });
      }
    }

    void fetchPosture(tenantId);
    return () => {
      cancelled = true;
    };
  }, [enabled, tenantId, generation]);

  // A trial must not stay "active" in a tab left open past its deadline: refetch
  // a moment after the deadline, and whenever the person returns to the app,
  // since access can also change server-side (a code approved, a trial ended
  // by an admin) while the tab was in the background.
  // The delay is measured when the effect runs, not while rendering: render
  // must not read the clock, and React Compiler would cache the reading.
  const trial = state.status === 'ready' ? state.trial : null;
  useEffect(() => {
    if (!enabled || trial === null) return;
    const refreshDelayMs = trialRefreshDelayMs(trial, now ?? new Date());
    if (refreshDelayMs === null) return;
    const timer = window.setTimeout(() => {
      setGeneration((current) => current + 1);
    }, refreshDelayMs);
    return () => {
      window.clearTimeout(timer);
    };
  }, [enabled, trial, now]);

  useEffect(() => {
    if (!enabled) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        setGeneration((current) => current + 1);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [enabled]);

  return state;
}
